import Box from "@mui/material/Box";
import { Component, type ReactNode } from "react";
import { ErrorStateView } from "./ErrorStateView.tsx";

/**
 * 翻訳キーと英語の既定文言を受け取り、表示する文字列を返す。
 *
 * AppErrorBoundary は I18nProvider の外側 (= 翻訳が使えない状態) に置くため、react-i18next の
 * useTranslation ではなくアプリの i18next インスタンスを直接呼ぶ関数を渡す。初期化前や
 * locale JSON の取得失敗時に翻訳キーがそのまま画面に出ないよう、未初期化なら fallback を返すこと。
 */
export type Translate = (key: string, fallback: string) => string;

const untranslated: Translate = (_key, fallback) => fallback;

/**
 * レンダリング例外時の全画面フォールバック。
 *
 * ヘッダー・フッターの外側 (AppErrorBoundary はルート 1 箇所) に描画されるため、
 * 単独でも意図した見た目になるようページ背景と全画面の中央寄せを自前で持つ。
 *
 * 案内は「再読み込み」に絞る。発火時点で URL 自体は有効でありアプリ側が壊れているだけのため
 * (docs/design/ゲストアプリ_エラー表示_詳細設計.md §7)。アプリ固有の 2 次案内 (guest のメール
 * での予約確認など) は translate 経由で説明文に含める。
 */
function CrashFallback({ t }: { t: Translate }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ErrorStateView
        severity="error"
        title={t("errorPage.unexpectedTitle", "Something went wrong")}
        description={t("errorPage.unexpectedDescription", "Please try reloading the page.")}
        actions={[
          {
            label: t("errorPage.reload", "Reload"),
            onClick: () => window.location.reload(),
          },
        ]}
      />
    </Box>
  );
}

type Props = {
  children: ReactNode;
  // 省略時は英語固定の文言を出す。
  translate?: Translate;
};
type State = { hasError: boolean };

/**
 * レンダリング中の例外を捕捉してアプリ全体の白画面を防ぐ (JPTP_CANSEI-1525)。
 *
 * ErrorBoundary は class component でしか書けない (getDerivedStateFromError)。
 * app/provider.tsx のルート 1 箇所にのみ置く。
 *
 * 拾えるのは「描画中の例外」だけで、API のエラーレスポンス (connect-query が error state に
 * するもの) は対象外。そちらは各アプリの取得エラー表示 (guest の QueryStateFallback など) が扱う。
 *
 * componentDidCatch は実装しない。React 19 は ErrorBoundary が捕捉した例外を既定の
 * onCaughtError で console.error に出力するため、自前のログは重複になる。
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override render() {
    if (this.state.hasError) {
      return <CrashFallback t={this.props.translate ?? untranslated} />;
    }
    return this.props.children;
  }
}
